import React, { useContext, useEffect, useMemo, useState } from 'react';
import CaseListPage from './CaseListPage';
import { AuthContext } from 'src/state/providers/AuthProvider';
import { KatSpinner, KatAlert } from '@amzn/katal-react';
import { logger } from 'src/logger';
import { useGetCaseListQuery } from 'src/state/slice/caseLifecycleApi';
import {
  IGetCaseListItem,
  IGetCaseListRequest,
} from 'src/state/slice/models/CaseLifecycleTypes';
import { CaseListConstants } from 'src/constants/ProjectConstants';
import {
  MAX_CASES_IN_EACH_BATCH,
  MIN_CASES_TO_FETCH,
} from 'src/constants/GlobalConstants';
import { TranslationContext } from 'src/state/providers/TranslationProvider';
import { useGetCaseListAllRecords } from './hooks/useGetCaseListAllRecords';

const CaseListWrapper = () => {
  const { customerId, clientId, preferredAccountId } = useContext(AuthContext);
  const [caseDetails, setCaseDetails] = useState<Array<IGetCaseListItem>>([]);
  const { messageBundle } = useContext(TranslationContext);

  const caseRequest: IGetCaseListRequest = {
    userId: customerId,
    accountId: preferredAccountId,
    clientId: clientId,
    pageSize: MAX_CASES_IN_EACH_BATCH,
  };

  const initialCaseRequest: IGetCaseListRequest = {
    ...caseRequest,
    pageSize: MIN_CASES_TO_FETCH,
  };

  //Request for fetching the initial data so that the user is not blocked.
  const {
    data: initialData,
    isError: initialDataFetchHasError,
    isLoading: initialDataFetchIsLoading,
    isFetching: initialDataIsFetching,
    error: initialDataFetchError,
    refetch: refetchInitialData,
  } = useGetCaseListQuery(initialCaseRequest);

  //Request for fetching the complete data.
  const { data, isError, error, isFetching, refetch, isLoading } =
    useGetCaseListAllRecords(caseRequest);

  useEffect(() => {
    //reset the api calls during the account id change.
    if (!(initialDataFetchIsLoading || initialDataFetchIsLoading)) {
      if (preferredAccountId) {
        refetchInitialData();
        refetch(caseRequest);
      }
    }
  }, [preferredAccountId]);

  useEffect(() => {
    if (data) {
      setCaseDetails(data.caseDetails);
    } else if (initialData && !data) {
      setCaseDetails(initialData.caseDetails);
    }
  }, [data, initialData]);

  //Show the spinner when data is loading during initial load
  //or during refetching when different accounts are selected
  if (initialDataFetchIsLoading || initialDataIsFetching) {
    return <KatSpinner />;
  }

  if (initialDataFetchHasError) {
    logger.error('Unable to load the cases. Error details and request', {
      initialCaseRequest,
      initialDataFetchError,
    });

    /**TODO: Replace this with the Global Sticky Error Message Container */
    return (
      <KatAlert
        persistent
        variant="danger"
        description={messageBundle.getMessage(
          CaseListConstants.UNABLE_TO_LOAD_CASES_MESSAGE,
        )}
      />
    );
  }
  if (isError) {
    logger.error(
      'Unable to load the cases for the complete request. Error details and request',
      {
        caseRequest,
        error,
      },
    );
  }
  return <CaseListPage data={caseDetails!} />;
};

export default CaseListWrapper;
